// Enzoic relay section of the Super Admin page (SaaS only). Customer-hosted installs in relay mode
// send their Enzoic exposure lookups here; a super admin issues each install a relay key, approves
// the domains it may look up, sets its domain limit and sees its recent requests.
//
// Backed by EnzoicRelayAdminController (api/v2/enzoic-relay-admin). Every route answers 404 when the
// relay server is turned off on this deployment, and the section then renders nothing.
//
// Rendered only inside the SuperAdmin page, which already redirects anyone who is not a super admin;
// the controller enforces SuperAdmin on every action.
//
// The relay key is a credential: it is held only in keyModal state while its modal is open and is
// never written to a toast, the URL, browser storage or the console.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Key, Plus, Check, X, Trash2, Edit2, Globe, List, RefreshCw, Copy } from "react-feather";
import { useApi } from "../hooks/useApi";
import Modal from "./Modal";
import LoadingSpinner from "./LoadingSpinner";

const BASE = "/enzoic-relay-admin";
const DEFAULT_MAX_DOMAINS = 10;
const REQUESTS_TAKE = 100;
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const OUTCOME_LABELS = {
  Served: "Served",
  NotFound: "No data",
  DomainNotApproved: "Not approved",
  DomainRejected: "Rejected",
  RateLimited: "Rate limited",
  UpstreamError: "Enzoic error",
  InvalidRequest: "Invalid request",
  InProgress: "In progress",
};

const domainStatusStyles = {
  Pending: "bg-yellow-100 text-yellow-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

// API times are zone-less UTC.
const formatTime = (value) => {
  if (!value) return "—";
  const text = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

// Pending first, then by domain (the API already sorts this way; kept here so the order holds).
export const sortDomains = (domains) =>
  [...domains].sort((a, b) => {
    const pa = a.status === "Pending" ? 0 : 1;
    const pb = b.status === "Pending" ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return a.domain < b.domain ? -1 : a.domain > b.domain ? 1 : 0;
  });

const errorCode = (err) => err?.response?.data?.code;
const errorStatus = (err) => err?.response?.status;

const clientMessage = (err, fallback) => {
  switch (errorCode(err)) {
    case "invalid_name":
      return "Enter a name of up to 200 characters.";
    case "invalid_max_domains":
      return "The domain limit must be between 1 and 1000.";
    case "busy":
      return "Another change to this client is in progress. Try again.";
    default:
      return errorStatus(err) === 404 ? "This client no longer exists." : fallback;
  }
};

const domainMessage = (err, maxDomains, fallback) => {
  if (errorCode(err) === "domain_limit_reached")
    return `Domain limit reached (${maxDomains}). Raise the limit to approve more.`;
  if (errorCode(err) === "busy") return "Another change to this client is in progress. Try again.";
  if (errorStatus(err) === 400) return "Not a valid domain.";
  if (errorStatus(err) === 404) return "This domain or client no longer exists.";
  return fallback;
};

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-input-bg border border-border text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50";
const rowBtnCls =
  "flex items-center gap-1 px-2.5 py-1 text-xs text-accent-cyan border border-accent-cyan/40 rounded-lg hover:bg-accent-cyan/10 transition-all disabled:opacity-50";
const dangerBtnCls =
  "flex items-center gap-1 px-2.5 py-1 text-xs text-risk-critical border border-risk-critical/40 rounded-lg hover:bg-risk-critical/10 transition-all disabled:opacity-50";
const approveBtnCls =
  "flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all disabled:opacity-50";
const primaryBtnCls =
  "flex items-center gap-1 px-4 py-2 bg-gradient-primary text-white text-sm rounded-lg hover:shadow-glow-cyan transition-all disabled:opacity-50 disabled:cursor-not-allowed";
const secondaryBtnCls =
  "px-4 py-2 text-sm text-content-secondary hover:text-content-primary bg-transparent border border-border rounded-lg hover:bg-theme-hover transition-all";
const thCls = "text-left px-4 py-3 text-sm font-medium text-content-secondary";
const tdCls = "px-4 py-3 text-sm text-content-secondary";

const ErrorLine = ({ children }) =>
  children ? (
    <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
      {children}
    </div>
  ) : null;

const SmallSpinner = () => <LoadingSpinner size="sm" className="!min-h-0 !bg-transparent py-6" />;

// The key modal: shows a newly issued key once, with a Copy button.
const KeyModal = ({ title, relayKey, onClose }) => {
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [manualHint, setManualHint] = useState(false);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const selectForManualCopy = () => {
    inputRef.current?.focus();
    inputRef.current?.select();
    setManualHint(true);
  };

  const handleCopy = async () => {
    if (!navigator.clipboard?.writeText) {
      selectForManualCopy();
      return;
    }
    try {
      await navigator.clipboard.writeText(relayKey);
      setCopied(true);
      setManualHint(false);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      selectForManualCopy();
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg">
      <p className="text-sm text-content-secondary mb-3">Copy this key now. It is not shown again.</p>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={relayKey}
          aria-label="Relay key"
          onFocus={(e) => e.target.select()}
          className={`${inputCls} font-mono min-w-0 flex-1`}
        />
        <button type="button" onClick={handleCopy} className={rowBtnCls}>
          <Copy className="h-3.5 w-3.5" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {manualHint && <p className="mt-1 text-xs text-content-muted">Press Ctrl+C to copy</p>}
      <div className="flex justify-end mt-6">
        <button type="button" onClick={onClose} className={secondaryBtnCls}>
          Close
        </button>
      </div>
    </Modal>
  );
};

const EnzoicRelayClients = () => {
  const apiCall = useApi();

  const [clients, setClients] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  // Create / edit
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", licenceId: "", maxDomains: String(DEFAULT_MAX_DOMAINS) });
  const [editClient, setEditClient] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", maxDomains: "" });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Key shown once; cleared when its modal closes.
  const [keyModal, setKeyModal] = useState(null);
  const [rotateClient, setRotateClient] = useState(null);

  // Domains modal
  const [domainsClientId, setDomainsClientId] = useState(null);
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainsError, setDomainsError] = useState(null);
  const [newDomain, setNewDomain] = useState("");
  const [domainBusy, setDomainBusy] = useState(false);

  // Requests modal
  const [requestsClient, setRequestsClient] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(null);

  // The section hides on 404 only on its first load (relay server off). A later 404, e.g. the
  // reload right after a key is shown, must not unmount the section and lose the one-time key.
  const initialLoadDone = useRef(false);

  const loadClients = useCallback(async () => {
    try {
      const data = await apiCall("GET", `${BASE}/clients`);
      initialLoadDone.current = true;
      setClients(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      if (!initialLoadDone.current && errorStatus(err) === 404) {
        setHidden(true);
        return;
      }
      initialLoadDone.current = true;
      setError("Failed to load relay clients.");
      setClients((prev) => prev ?? []);
    }
  }, [apiCall]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const loadDomains = useCallback(
    async (clientId) => {
      setDomainsLoading(true);
      try {
        const data = await apiCall("GET", `${BASE}/clients/${clientId}/domains`);
        setDomains(sortDomains(Array.isArray(data) ? data : []));
      } catch {
        setDomainsError("Failed to load domains.");
      } finally {
        setDomainsLoading(false);
      }
    },
    [apiCall]
  );

  if (hidden) return null;
  if (clients === null && !error) return null;

  const domainsClient = clients?.find((c) => c.id === domainsClientId) ?? null;

  // ---- Create ----
  const openCreate = () => {
    setCreateForm({ name: "", licenceId: "", maxDomains: String(DEFAULT_MAX_DOMAINS) });
    setFormError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const name = createForm.name.trim();
    const licenceId = createForm.licenceId.trim();
    const maxDomains = Number(createForm.maxDomains);
    if (!name) {
      setFormError("Enter a name.");
      return;
    }
    if (licenceId && !GUID_RE.test(licenceId)) {
      setFormError("The licence ID must be a GUID, or left empty.");
      return;
    }
    if (!Number.isInteger(maxDomains) || maxDomains < 1 || maxDomains > 1000) {
      setFormError("The domain limit must be between 1 and 1000.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await apiCall("POST", `${BASE}/clients`, {
        name,
        licenceId: licenceId || null,
        maxDomains,
      });
      setCreateOpen(false);
      if (res?.key) setKeyModal({ title: `Relay key for ${res.client?.name ?? name}`, key: res.key });
      await loadClients();
    } catch (err) {
      setFormError(clientMessage(err, "Failed to create the relay client."));
    } finally {
      setSaving(false);
    }
  };

  // ---- Edit ----
  const openEdit = (client) => {
    setEditForm({ name: client.name, maxDomains: String(client.maxDomains) });
    setFormError(null);
    setEditClient(client);
  };

  const handleEdit = async () => {
    const name = editForm.name.trim();
    const maxDomains = Number(editForm.maxDomains);
    if (!name) {
      setFormError("Enter a name.");
      return;
    }
    if (!Number.isInteger(maxDomains) || maxDomains < 1 || maxDomains > 1000) {
      setFormError("The domain limit must be between 1 and 1000.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await apiCall("PATCH", `${BASE}/clients/${editClient.id}`, { name, maxDomains });
      setEditClient(null);
      await loadClients();
    } catch (err) {
      setFormError(clientMessage(err, "Failed to save the relay client."));
    } finally {
      setSaving(false);
    }
  };

  // ---- Enable / disable ----
  const handleToggle = async (client) => {
    setActioningId(client.id);
    try {
      await apiCall("PATCH", `${BASE}/clients/${client.id}`, { isEnabled: !client.isEnabled });
      await loadClients();
    } catch (err) {
      setError(clientMessage(err, "Failed to update the relay client."));
    } finally {
      setActioningId(null);
    }
  };

  // ---- Rotate ----
  const handleRotate = async () => {
    const client = rotateClient;
    setRotateClient(null);
    setActioningId(client.id);
    try {
      const res = await apiCall("POST", `${BASE}/clients/${client.id}/rotate-key`, null);
      if (res?.key) setKeyModal({ title: `New relay key for ${client.name}`, key: res.key });
      await loadClients();
    } catch (err) {
      setError(clientMessage(err, "Failed to rotate the key."));
    } finally {
      setActioningId(null);
    }
  };

  // ---- Domains ----
  const openDomains = (client) => {
    setDomains([]);
    setDomainsError(null);
    setNewDomain("");
    setDomainsClientId(client.id);
    loadDomains(client.id);
  };

  const closeDomains = () => {
    setDomainsClientId(null);
    setDomains([]);
    setDomainsError(null);
    setNewDomain("");
  };

  const runDomainAction = async (call, fallback) => {
    setDomainBusy(true);
    setDomainsError(null);
    try {
      await call();
      await Promise.all([loadDomains(domainsClientId), loadClients()]);
      return true;
    } catch (err) {
      setDomainsError(domainMessage(err, domainsClient?.maxDomains, fallback));
      return false;
    } finally {
      setDomainBusy(false);
    }
  };

  const handleApprove = (d) =>
    runDomainAction(() => apiCall("POST", `${BASE}/domains/${d.id}/approve`, null), "Failed to approve the domain.");
  const handleReject = (d) =>
    runDomainAction(() => apiCall("POST", `${BASE}/domains/${d.id}/reject`, null), "Failed to reject the domain.");
  const handleRemove = (d) =>
    runDomainAction(() => apiCall("DELETE", `${BASE}/domains/${d.id}`), "Failed to remove the domain.");
  const handleAddDomain = async () => {
    const domain = newDomain.trim();
    if (!domain) return;
    const ok = await runDomainAction(
      () => apiCall("POST", `${BASE}/clients/${domainsClientId}/domains`, { domain }),
      "Failed to add the domain."
    );
    if (ok) setNewDomain("");
  };

  // ---- Requests ----
  const openRequests = async (client) => {
    setRequests([]);
    setRequestsError(null);
    setRequestsClient(client);
    setRequestsLoading(true);
    try {
      const data = await apiCall("GET", `${BASE}/clients/${client.id}/requests?take=${REQUESTS_TAKE}`);
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequestsError("Failed to load requests.");
    } finally {
      setRequestsLoading(false);
    }
  };

  const closeRequests = () => {
    setRequestsClient(null);
    setRequests([]);
    setRequestsError(null);
  };

  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-content-primary mb-1">
            <Key size={18} className="text-accent-cyan" />
            Enzoic relay
          </h2>
          <p className="text-sm text-content-secondary">
            Customer-hosted installs that use dark web monitoring through this service.
          </p>
        </div>
        <button type="button" onClick={openCreate} className={primaryBtnCls}>
          <Plus size={16} />
          New client
        </button>
      </div>

      <ErrorLine>{error}</ErrorLine>

      <div className="bg-theme-secondary border border-border rounded-xl overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-theme-primary/50">
              {["Name", "Key", "Domains", "Last used", "Status", "Actions"].map((h) => (
                <th key={h} className={thCls}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(clients ?? []).map((c) => (
              <tr key={c.id} className="border-b border-border last:border-b-0 hover:bg-theme-hover transition-colors">
                <td className="px-4 py-3 text-sm text-content-primary font-medium">{c.name}</td>
                <td className={`${tdCls} font-mono`}>{`axr_${c.keyPrefix}_…`}</td>
                <td className={tdCls}>
                  <div className="flex items-center gap-2">
                    <span>{`${c.approvedDomains} / ${c.maxDomains}`}</span>
                    {c.pendingDomains > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {`${c.pendingDomains} pending`}
                      </span>
                    )}
                  </div>
                </td>
                <td className={tdCls}>{formatTime(c.lastUsedAt)}</td>
                <td className={tdCls}>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.isEnabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {c.isEnabled ? "Enabled" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => openDomains(c)} className={rowBtnCls} aria-label={`Domains for ${c.name}`}>
                      <Globe className="h-3.5 w-3.5" />
                      Domains
                    </button>
                    <button type="button" onClick={() => openRequests(c)} className={rowBtnCls} aria-label={`Requests for ${c.name}`}>
                      <List className="h-3.5 w-3.5" />
                      Requests
                    </button>
                    <button
                      type="button"
                      onClick={() => setRotateClient(c)}
                      disabled={actioningId === c.id}
                      className={rowBtnCls}
                      aria-label={`Rotate key for ${c.name}`}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Rotate key
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggle(c)}
                      disabled={actioningId === c.id}
                      className={c.isEnabled ? dangerBtnCls : rowBtnCls}
                      aria-label={`${c.isEnabled ? "Disable" : "Enable"} ${c.name}`}
                    >
                      {c.isEnabled ? "Disable" : "Enable"}
                    </button>
                    <button type="button" onClick={() => openEdit(c)} className={rowBtnCls} aria-label={`Edit ${c.name}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {clients?.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-center text-sm text-content-muted">
                  No relay clients yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* New client */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="New relay client" size="md">
        <ErrorLine>{formError}</ErrorLine>
        <div className="space-y-4">
          <label className="block text-sm text-content-secondary">
            Name
            <input
              type="text"
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={200}
              aria-label="Name"
              className={`${inputCls} mt-1`}
            />
          </label>
          <label className="block text-sm text-content-secondary">
            Licence ID (optional)
            <input
              type="text"
              value={createForm.licenceId}
              onChange={(e) => setCreateForm((f) => ({ ...f, licenceId: e.target.value }))}
              aria-label="Licence ID"
              className={`${inputCls} mt-1 font-mono`}
            />
          </label>
          <label className="block text-sm text-content-secondary">
            Domain limit
            <input
              type="number"
              min={1}
              max={1000}
              value={createForm.maxDomains}
              onChange={(e) => setCreateForm((f) => ({ ...f, maxDomains: e.target.value }))}
              aria-label="Domain limit"
              className={`${inputCls} mt-1`}
            />
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={() => setCreateOpen(false)} disabled={saving} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="button" onClick={handleCreate} disabled={saving || !createForm.name.trim()} className={primaryBtnCls}>
            Create client
          </button>
        </div>
      </Modal>

      {/* Edit client */}
      <Modal isOpen={!!editClient} onClose={() => setEditClient(null)} title="Edit relay client" size="md">
        <ErrorLine>{formError}</ErrorLine>
        <div className="space-y-4">
          <label className="block text-sm text-content-secondary">
            Name
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={200}
              aria-label="Name"
              className={`${inputCls} mt-1`}
            />
          </label>
          <label className="block text-sm text-content-secondary">
            Domain limit
            <input
              type="number"
              min={1}
              max={1000}
              value={editForm.maxDomains}
              onChange={(e) => setEditForm((f) => ({ ...f, maxDomains: e.target.value }))}
              aria-label="Domain limit"
              className={`${inputCls} mt-1`}
            />
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={() => setEditClient(null)} disabled={saving} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="button" onClick={handleEdit} disabled={saving} className={primaryBtnCls}>
            Save
          </button>
        </div>
      </Modal>

      {/* Rotate confirm */}
      <Modal isOpen={!!rotateClient} onClose={() => setRotateClient(null)} title="Rotate relay key" size="md">
        <p className="text-sm text-content-secondary mb-6">
          Rotate the key for <span className="font-semibold text-content-primary">{rotateClient?.name}</span>? The
          current key stops working immediately.
        </p>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => setRotateClient(null)} className={secondaryBtnCls}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRotate}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-risk-critical rounded-lg hover:bg-risk-critical/90 transition-all"
          >
            <RefreshCw className="h-4 w-4" />
            Rotate key
          </button>
        </div>
      </Modal>

      {/* Key, shown once */}
      {keyModal && <KeyModal title={keyModal.title} relayKey={keyModal.key} onClose={() => setKeyModal(null)} />}

      {/* Domains */}
      <Modal
        isOpen={domainsClientId !== null}
        onClose={closeDomains}
        title={`Domains: ${domainsClient?.name ?? ""}`}
        size="3xl"
      >
        {domainsClient && (
          <p className="text-sm text-content-secondary mb-4">
            {`${domainsClient.approvedDomains} of ${domainsClient.maxDomains} approved domains used.`}
          </p>
        )}
        <ErrorLine>{domainsError}</ErrorLine>
        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddDomain();
            }}
            placeholder="example.com"
            aria-label="Add domain"
            className={`${inputCls} min-w-0 flex-1`}
          />
          <button type="button" onClick={handleAddDomain} disabled={domainBusy || !newDomain.trim()} className={primaryBtnCls}>
            <Plus size={16} />
            Add domain
          </button>
        </div>
        {domainsLoading && domains.length === 0 ? (
          <SmallSpinner />
        ) : (
          <div className="border border-border rounded-xl overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-theme-primary/50">
                  {["Domain", "Status", "Requested", "Last queried", "Queries", "Actions"].map((h) => (
                    <th key={h} className={thCls}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {domains.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-b-0" data-testid="relay-domain-row">
                    <td className="px-4 py-3 text-sm text-content-primary font-mono">{d.domain}</td>
                    <td className={tdCls}>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          domainStatusStyles[d.status] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {d.status}
                      </span>
                    </td>
                    <td className={tdCls}>{formatTime(d.requestedAt)}</td>
                    <td className={tdCls}>{formatTime(d.lastQueriedAt)}</td>
                    <td className={tdCls}>{d.queryCount}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {d.status === "Pending" ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprove(d)}
                              disabled={domainBusy}
                              className={approveBtnCls}
                              aria-label={`Approve ${d.domain}`}
                            >
                              <Check className="h-3.5 w-3.5" />
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReject(d)}
                              disabled={domainBusy}
                              className={dangerBtnCls}
                              aria-label={`Reject ${d.domain}`}
                            >
                              <X className="h-3.5 w-3.5" />
                              Reject
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRemove(d)}
                            disabled={domainBusy}
                            className={dangerBtnCls}
                            aria-label={`Remove ${d.domain}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!domainsLoading && domains.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-6 text-center text-sm text-content-muted">
                      No domains yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Requests */}
      <Modal
        isOpen={!!requestsClient}
        onClose={closeRequests}
        title={`Recent requests: ${requestsClient?.name ?? ""}`}
        size="3xl"
      >
        <ErrorLine>{requestsError}</ErrorLine>
        {requestsLoading ? (
          <SmallSpinner />
        ) : (
          <div className="border border-border rounded-xl overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-theme-primary/50">
                  {["Time", "Domain", "Outcome"].map((h) => (
                    <th key={h} className={thCls}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((r, i) => (
                  <tr key={`${r.requestedAt}-${i}`} className="border-b border-border last:border-b-0">
                    <td className={tdCls}>{formatTime(r.requestedAt)}</td>
                    <td className="px-4 py-3 text-sm text-content-primary font-mono">{r.domain}</td>
                    <td className={tdCls}>{OUTCOME_LABELS[r.outcome] ?? r.outcome}</td>
                  </tr>
                ))}
                {requests.length === 0 && !requestsError && (
                  <tr>
                    <td colSpan={3} className="px-6 py-6 text-center text-sm text-content-muted">
                      No requests yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnzoicRelayClients;
